/**
 * ============================================================
 * kanbanSlice.ts — Estado das Colunas do Kanban (Pipeline)
 * ============================================================
 *
 * Responsabilidade: Gerenciar a definicao das colunas do quadro
 *   kanban do pipeline CRM: adicionar, remover, reordenar e
 *   alterar cores das colunas.
 * Arquitetura: Slice do Zustand que compoe a store global.
 *   As colunas definem os estagios do funil de vendas.
 *   Quando uma coluna e deletada, os deals nela sao movidos (pelo hook de mutação)
 *   automaticamente para a primeira coluna restante (fallback).
 * Padroes: Zustand Slice, Cross-slice access (acessa deals para
 *   reatribuir ao deletar coluna), Helper reutilizavel (reorderByIndex)
 * Dependencias:
 *   - Depende de: ../types, @/lib/constants, ../helpers
 *   - Quem depende dele: useStore, componentes de kanban board,
 *     dealsSlice (moveDeal consulta kanbanColumns para labels)
 *
 * ============================================================
 */

import type { StateCreator } from 'zustand';
import type { KanbanColumnDef } from '../types';
import { DEFAULT_KANBAN_COLUMNS } from '@/lib/constants';
import { reorderByIndex } from '../helpers';

// ── Interface do Slice ──
export interface KanbanSlice {
  kanbanColumns: KanbanColumnDef[];
  setKanbanColumns: (cols: KanbanColumnDef[]) => void;
  addKanbanColumn: (col: KanbanColumnDef) => void;
  deleteKanbanColumn: (colId: string) => void;
  reorderKanbanColumns: (fromIndex: number, toIndex: number) => void;
  updateKanbanColumnColor: (colId: string, color: string) => void;
}

// ── Criacao do Slice ──
export const createKanbanSlice: StateCreator<KanbanSlice, [], [], KanbanSlice> = (set) => ({
  // Estado inicial usa colunas padrao definidas em constants
  kanbanColumns: DEFAULT_KANBAN_COLUMNS,
  setKanbanColumns: (cols) => set({ kanbanColumns: cols }),
  addKanbanColumn: (col) => set((s) => ({ kanbanColumns: [...s.kanbanColumns, col] })),

  // Só remove a coluna do estado. Os leads órfãos são reatribuídos por
  // `useKanbanColumnMutations.deleteColumn` (no banco e no cache do React
  // Query) — a store não guarda leads.
  deleteKanbanColumn: (colId) =>
    set((s) => ({ kanbanColumns: s.kanbanColumns.filter((c) => c.id !== colId) })),

  // Reordenacao via drag-and-drop (usa helper generico)
  reorderKanbanColumns: (fromIndex, toIndex) =>
    set((s) => ({ kanbanColumns: reorderByIndex(s.kanbanColumns, fromIndex, toIndex) })),

  // Personalizacao de cor por coluna (exibida no header do kanban)
  updateKanbanColumnColor: (colId, color) =>
    set((s) => ({
      kanbanColumns: s.kanbanColumns.map((c) => (c.id === colId ? { ...c, color } : c)),
    })),
});
